/**
 * A file given with an answer (05 §1, decision 10): uploaded onto the case
 * at once — it lands in Files, entered at the next run — and named by the
 * answer the person records. One file, never an archive; detaching leaves
 * it in Files, unnamed by the answer.
 */

import { useState } from 'react';

import { Button, FileButton, Pill } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPaperclip } from '@tabler/icons-react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import { CONVERSATION_ATTACHMENT_MIME } from '@/types/attachment';
import { toError } from '@/utils/errors';

export interface AttachedFile {
  /** The attachment's id — what the answer carries. */
  id: string;
  filename: string;
}

/** What an answer's file may be: the chat's kinds, less the archives. */
export const ANSWER_FILE_MIME = CONVERSATION_ATTACHMENT_MIME.filter((m) => !m.includes('zip')).join(
  ','
);

export function AttachFile({
  reportId,
  attached,
  onChange,
}: Readonly<{
  reportId: string;
  attached: AttachedFile | null;
  onChange: (file: AttachedFile | null) => void;
}>) {
  const [busy, setBusy] = useState(false);

  const attach = async (file: File | null) => {
    if (!file) {
      return;
    }
    if (file.name.toLowerCase().endsWith('.zip')) {
      notifications.show({ color: 'red', message: 'Attach one file, not an archive.' });
      return;
    }
    setBusy(true);
    try {
      const made = await vnextApiClient.uploadWorkbenchFiles(reportId, [file]);
      const id = made.uploaded[0];
      if (!id) {
        throw new Error('The upload made no file');
      }
      onChange({ id, filename: file.name });
    } catch (err) {
      Sentry.captureException(toError(err));
      notifications.show({
        color: 'red',
        message: toError(err, 'Could not upload the file').message,
      });
    } finally {
      setBusy(false);
    }
  };

  if (attached) {
    return (
      <Pill
        size="sm"
        withRemoveButton
        onRemove={() => onChange(null)}
        removeButtonProps={{ 'aria-label': 'Detach the file' }}
        data-testid="attached-file"
      >
        {attached.filename}
      </Pill>
    );
  }
  return (
    <FileButton accept={ANSWER_FILE_MIME} onChange={(file) => void attach(file)}>
      {(props) => (
        <Button
          {...props}
          size="xs"
          variant="subtle"
          leftSection={<IconPaperclip size={14} />}
          loading={busy}
          data-testid="attach-file"
        >
          Attach a file
        </Button>
      )}
    </FileButton>
  );
}
